import {HTTPClient as HTTPClientCore, IRequestConfig} from "@fanhubmedia/fe-common-utils";
import axios, {AxiosError} from "axios";
import {isObject, set} from "lodash";
import ApiError from "./ApiError";
import {API_URL, JSON_URL, PROJECT_JSON_URL} from "modules/constants";
import {IApiResponse, IDictionary} from "modules/types";
import {CANCEL} from "redux-saga";
import i18n from "i18next";
import {IFaq, IHelpPages, IUser} from "modules/reducers";
import {IUserBackdoorLoginPayload} from "modules/actions/user";

class HTTPClient extends HTTPClientCore {
	/**
	 * Overridden method adds CancelToken symbol, that allow redux-saga'
	 * "takeLatest" function to cancel any requests automatically.
	 * http://fe-common-utils.s3-website-eu-west-1.amazonaws.com/classes/httpclient.html
	 */
	public makeRequest<T>(config: IRequestConfig): Promise<T> {
		const source = axios.CancelToken.source();

		const request = super.makeRequest<T>({
			...config,
			cancelToken: source.token,
		});

		return set<Promise<T>>(request, CANCEL, () => source.cancel());
	}
}

const onCatchNetworkError = ({response, message = "Network error"}: AxiosError<ApiError>) => {
	const data = response?.data;
	const error = isObject(data)
		? data
		: {
				errors: [new ApiError(message)],
		  };

	return Promise.reject(error).catch((err) => ApiError.CHECK(err as IApiResponse));
};

export const getLang = () => {
	const currentLang = i18n.language;

	return currentLang || "en";
};

export const APIClient = new HTTPClient({
	baseURL: API_URL,
	withCredentials: true,
	onCatchNetworkError,
});

export const JSONClient = new HTTPClient({
	baseURL: JSON_URL,
});

export const ProjectJSONClient = new HTTPClient({
	baseURL: PROJECT_JSON_URL,
});

type TUserResponse = IApiResponse<{user: IUser}>;

export const Api = {
	JSON: {
		checksums: () => JSONClient.get<IDictionary<string>>("checksums.json"),
		helpPages: () => ProjectJSONClient.get<IHelpPages>("help_pages.json"),
		faq: () => ProjectJSONClient.get<IFaq[]>("faq.json"),
	},
	Auth: {
		backdoor: (params: IUserBackdoorLoginPayload) =>
			APIClient.post<TUserResponse>(`${getLang()}/auth/backdoor`, params),
		preregistration: (params: IUserBackdoorLoginPayload) =>
			APIClient.post<IApiResponse>(`${getLang()}/auth/preregistration`, params),
	},
	User: {
		user: () => APIClient.get<TUserResponse>(`${getLang()}/user`),
	},
	Predictions: {},
	Rankings: {},
	Contact: {},
	Leagues: {},
	Common: {
		time: () => APIClient.get<IApiResponse<{time: string}>>(`${getLang()}/time/current`),
	},
};

export * from "./ApiError";

export default Api;
